
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import PageShell from "../components/PageShell";
import "./EmployeeDirectory.css";

/**
 * AdminPeople (Steps 2 + 3)
 * Step 2: Assign employees to managers (reporting relationships)
 * Step 3: Invite managers/employees + CSV import + company policies + activity feed
 */
export default function AdminPeople() {
  const [users, setUsers] = useState([]);

  const [managers, setManagers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Add user (direct) — runbook Part 3: name, email, role, password → POST /api/admin/users
  const [addUserName, setAddUserName] = useState("");
  const [addUserEmail, setAddUserEmail] = useState("");
  const [addUserRole, setAddUserRole] = useState("team_manager");
  const [addUserPassword, setAddUserPassword] = useState("");
  const [addUserBusy, setAddUserBusy] = useState(false);
  const [addUserError, setAddUserError] = useState("");

  // Step 3: Invite
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("team_manager");
  const [inviteManagerId, setInviteManagerId] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Step 3: Policies
  const [policiesBusy, setPoliciesBusy] = useState(false);
  const [domains, setDomains] = useState(""); // comma-separated
  const [quota, setQuota] = useState(50);
  const [approvalsRequired, setApprovalsRequired] = useState(false);

  // Step 3: CSV import
  const [importBusy, setImportBusy] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // Step 3: Activity
  const [activity, setActivity] = useState([]);
  const [activityBusy, setActivityBusy] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewsBusy, setReviewsBusy] = useState(false);
  const [reviewStatus, setReviewStatus] = useState("pending");
  const [reviewMode, setReviewMode] = useState("all");
  const [reviewRecipientQuery, setReviewRecipientQuery] = useState("");
  const [reviewMinConfidence, setReviewMinConfidence] = useState("");

  const fetchPeople = async () => {
    const [uRes, mRes] = await Promise.all([
      axios.get("/api/admin/users", { withCredentials: true }),
      axios.get("/api/admin/managers", { withCredentials: true }),
    ]);
    setUsers(Array.isArray(uRes.data) ? uRes.data : []);
    setManagers(Array.isArray(mRes.data) ? mRes.data : []);
  };

  const fetchPolicies = async () => {
    const res = await axios.get("/api/admin/policies", {
      withCredentials: true,
    });
    const p = res.data || {};
    setDomains(
      Array.isArray(p.allowed_email_domains)
        ? p.allowed_email_domains.join(", ")
        : "",
    );
    setQuota(Number(p.invite_quota_per_day || 50));
    setApprovalsRequired(Boolean(p.approvals_required));
  };

  const fetchActivity = async () => {
    const res = await axios.get("/api/admin/activity?limit=30", {
      withCredentials: true,
    });
    setActivity(Array.isArray(res.data) ? res.data : []);
  };

  const fetchInvites = async () => {
    const res = await axios.get("/api/admin/invites?status=pending&limit=50", {
      withCredentials: true,
    });
    setInvites(Array.isArray(res.data) ? res.data : []);
  };

  const fetchReviews = async () => {
    const params = { status: reviewStatus, limit: 50 };
    if (reviewMode !== "all") params.detection_mode = reviewMode;
    if (reviewRecipientQuery.trim()) params.recipient_query = reviewRecipientQuery.trim();
    if (reviewMinConfidence !== "" && !Number.isNaN(Number(reviewMinConfidence))) {
      params.min_confidence = Number(reviewMinConfidence);
    }
    const res = await axios.get("/api/admin/appreciation-reviews", {
      withCredentials: true,
      params,
    });
    setReviews(Array.isArray(res.data?.items) ? res.data.items : []);
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError("");
      setNotice("");
      await fetchPeople();
      await fetchPolicies();
      await fetchActivity();
      await fetchInvites();
      await fetchReviews();
    } catch (e) {
      setError("Failed to load admin people data.");
      setUsers([]);
      setManagers([]);
      setInvites([]);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchReviews().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewStatus, reviewMode]);

  const managerOptions = useMemo(() => {
    const opts = [{ id: "", name: "Unassigned" }];
    managers.forEach((m) =>
      opts.push({ id: m.id, name: m.name || m.email || m.id }),
    );
    return opts;
  }, [managers]);

  const employees = useMemo(
    () => users.filter((u) => u.role === "user"),
    [users],
  );
  const managersList = useMemo(
    () => users.filter((u) => u.role === "team_manager"),
    [users],
  );

  const setManager = async (employeeId, managerId) => {
    try {
      setSavingId(employeeId);
      setError("");
      setNotice("");

      await axios.put(
        `/api/admin/users/${employeeId}/manager`,
        { manager_id: managerId || null },
        { withCredentials: true },
      );

      setNotice("Manager assignment updated.");
      await fetchPeople();
      await fetchActivity();
    } catch (e) {
      setError("Failed to update manager assignment.");
    } finally {
      setSavingId(null);
    }
  };

  const createUser = async () => {
    setAddUserError("");
    if (!addUserEmail.trim() || !addUserPassword) {
      setAddUserError("Email and password are required.");
      return;
    }
    setAddUserBusy(true);
    try {
      await axios.post(
        "/api/admin/users",
        {
          name: addUserName.trim() || addUserEmail.trim(),
          email: addUserEmail.trim(),
          role: addUserRole,
          password: addUserPassword,
        },
        { withCredentials: true },
      );
      setNotice("User created. They can sign in with that email and password.");
      setAddUserName("");
      setAddUserEmail("");
      setAddUserRole("team_manager");
      setAddUserPassword("");
      await fetchPeople();
    } catch (e) {
      setAddUserError(
        e?.response?.data?.error || "Failed to create user.",
      );
    } finally {
      setAddUserBusy(false);
    }
  };

  const createInvite = async () => {
    try {
      setInviteBusy(true);
      setError("");
      setNotice("");
      setInviteResult(null);

      const payload = {
        email: inviteEmail.trim(),
        role: inviteRole,
      };
      if (inviteRole === "user" && inviteManagerId)
        payload.manager_id = inviteManagerId;

      const res = await axios.post("/api/invites", payload, {
        withCredentials: true,
      });
      setInviteResult(res.data);
      setNotice(
        res.data?.email_sent
          ? "Invite created and emailed. You can also copy the link below."
          : "Invite created. Copy the link below.",
      );
      setInviteEmail("");
      setInviteManagerId("");
      await fetchActivity();
      await fetchInvites();
    } catch (e) {
      const msg = e?.response?.data?.error || "Failed to create invite.";
      setError(msg);
    } finally {
      setInviteBusy(false);
    }
  };

  const savePolicies = async () => {
    try {
      setPoliciesBusy(true);
      setError("");
      setNotice("");

      const payload = {
        allowed_email_domains: domains,
        invite_quota_per_day: quota,
        approvals_required: approvalsRequired,
      };

      await axios.put("/api/admin/policies", payload, {
        withCredentials: true,
      });
      setNotice("Policies updated.");
      await fetchActivity();
    } catch (e) {
      setError("Failed to update policies.");
    } finally {
      setPoliciesBusy(false);
    }
  };

  const runImport = async () => {
    if (!importFile) {
      setError("Please choose a CSV file first.");
      return;
    }
    try {
      setImportBusy(true);
      setError("");
      setNotice("");
      setImportResult(null);

      const form = new FormData();
      form.append("file", importFile);

      const res = await axios.post("/api/admin/import/users", form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setImportResult(res.data);
      setNotice("CSV processed. Review created invite links below.");
      await fetchActivity();
      await fetchInvites();
    } catch (e) {
      const msg = e?.response?.data?.error || "CSV import failed.";
      setError(msg);
    } finally {
      setImportBusy(false);
    }
  };

  const refreshActivity = async () => {
    try {
      setActivityBusy(true);
      await fetchActivity();
    } finally {
      setActivityBusy(false);
    }
  };

  const decideReview = async (reviewId, action) => {
    try {
      setError("");
      setNotice("");
      await axios.post(
        `/api/admin/appreciation-reviews/${reviewId}/${action}`,
        {},
        { withCredentials: true },
      );
      setNotice(action === "approve" ? "Recognition approved and delivered." : "Recognition rejected.");
      await fetchReviews();
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to update review.");
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice("Copied to clipboard.");
    } catch {
      // ignore
    }
  };

  return (
    <PageShell
      title="People"
      subtitle="Assign employees to managers. Invite new users, enforce onboarding policies, and review activity."
    >
      {error ? (
        <div className="pill edAlert edAlert--error">{error}</div>
      ) : null}
      {notice ? (
        <div className="pill edAlert edAlert--success">{notice}</div>
      ) : null}

      {loading ? (
        <div className="card">
          <div className="card__body">Loading…</div>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card__body">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>Managers</div>
                  <div style={{ opacity: 0.8, fontSize: 13 }}>
                    {managersList.length} manager
                    {managersList.length === 1 ? "" : "s"}
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 700 }}>Employees</div>
                  <div style={{ opacity: 0.8, fontSize: 13 }}>
                    {employees.length} employee
                    {employees.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Add user (direct) — runbook Part 3 */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card__body">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Add user
              </div>
              <div className="edReadOnly" style={{ marginBottom: 12 }}>
                Create a user with email and password. They can sign in immediately.
              </div>
              {addUserError ? (
                <div className="pill edAlert edAlert--error" style={{ marginBottom: 12 }}>
                  {addUserError}
                </div>
              ) : null}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "end",
                }}
              >
                <div style={{ minWidth: 180 }}>
                  <label className="label" htmlFor="add-user-name">Name</label>
                  <input
                    id="add-user-name"
                    className="input"
                    value={addUserName}
                    onChange={(e) => setAddUserName(e.target.value)}
                    placeholder="Full name"
                    disabled={addUserBusy}
                    data-testid="add-user-name"
                  />
                </div>
                <div style={{ minWidth: 260 }}>
                  <label className="label" htmlFor="add-user-email">Email</label>
                  <input
                    id="add-user-email"
                    className="input"
                    type="email"
                    value={addUserEmail}
                    onChange={(e) => setAddUserEmail(e.target.value)}
                    placeholder="person@company.com"
                    disabled={addUserBusy}
                    data-testid="add-user-email"
                  />
                </div>
                <div style={{ minWidth: 200 }}>
                  <label className="label" htmlFor="add-user-role">Role</label>
                  <select
                    id="add-user-role"
                    className="input"
                    value={addUserRole}
                    onChange={(e) => setAddUserRole(e.target.value)}
                    disabled={addUserBusy}
                    data-testid="add-user-role"
                  >
                    <option value="team_manager">Team Manager</option>
                    <option value="user">Employee</option>
                  </select>
                </div>
                <div style={{ minWidth: 180 }}>
                  <label className="label" htmlFor="add-user-password">Password</label>
                  <input
                    id="add-user-password"
                    className="input"
                    type="password"
                    value={addUserPassword}
                    onChange={(e) => setAddUserPassword(e.target.value)}
                    placeholder="Required"
                    disabled={addUserBusy}
                    data-testid="add-user-password"
                  />
                </div>
                <button
                  className="btn primary"
                  type="button"
                  onClick={createUser}
                  disabled={addUserBusy || !addUserEmail.trim() || !addUserPassword}
                  data-testid="add-user-submit"
                >
                  {addUserBusy ? "Adding…" : "Add user"}
                </button>
              </div>
            </div>
          </div>

          {/* Step 3: Invites */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card__body">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Invite a manager or employee
              </div>
              <div className="edReadOnly" style={{ marginBottom: 12 }}>
                Creates a secure invite. If email is configured, we will send
                the link automatically; otherwise copy it below.
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "end",
                }}
              >
                <div style={{ minWidth: 260 }}>
                  <label className="label">Email</label>
                  <input
                    className="input"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="person@company.com"
                    disabled={inviteBusy}
                  />
                </div>

                <div style={{ minWidth: 200 }}>
                  <label className="label">Role</label>
                  <select
                    className="input"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    disabled={inviteBusy}
                  >
                    <option value="team_manager">Team Manager</option>
                    <option value="user">Employee</option>
                  </select>
                </div>

                {inviteRole === "user" ? (
                  <div style={{ minWidth: 240 }}>
                    <label className="label">Assign manager (optional)</label>
                    <select
                      className="input"
                      value={inviteManagerId}
                      onChange={(e) => setInviteManagerId(e.target.value)}
                      disabled={inviteBusy}
                    >
                      {managerOptions.map((m) => (
                        <option key={m.id || "none"} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <button
                  className="btn primary"
                  onClick={createInvite}
                  disabled={inviteBusy || !inviteEmail.trim()}
                >
                  {inviteBusy ? "Creating…" : "Create invite"}
                </button>
              </div>

              {inviteResult?.accept_url ? (
                <div style={{ marginTop: 14 }}>
                  <div className="edReadOnly" style={{ marginBottom: 6 }}>
                    Invite link
                  </div>
                  <div className="edReadOnly" style={{ marginBottom: 10 }}>
                    {inviteResult?.email_sent
                      ? "Email sent to " + inviteResult.email
                      : inviteResult?.email_error
                        ? "Email not sent: " + inviteResult.email_error
                        : "Email not sent (email not configured)."}
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <code
                      style={{
                        padding: "8px 10px",
                        borderRadius: 8,
                        background: "var(--card-soft)",
                        flex: "1 1 480px",
                      }}
                    >
                      {inviteResult.accept_url}
                    </code>
                    <button
                      className="btn"
                      onClick={() => copy(inviteResult.accept_url)}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              ) : null}

              {/* Pending invites */}
              <div style={{ marginTop: 18 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>
                  Pending invites
                </div>
                <div className="edReadOnly" style={{ marginBottom: 10 }}>
                  These are unaccepted invites in your workspace. "Resend"
                  generates a new link and attempts to email it.
                </div>

                {invites.length === 0 ? (
                  <div className="edEmpty">No pending invites.</div>
                ) : (
                  <div className="edTableWrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Invited by</th>
                          <th>Created</th>
                          <th>Expires</th>
                          <th style={{ width: 240 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invites.map((it) => (
                          <tr key={it.id}>
                            <td>{it.email}</td>
                            <td>
                              {it.role === "team_manager"
                                ? "Team Manager"
                                : "Employee"}
                            </td>
                            <td>
                              {it.invited_by_user_id
                                ? it.invited_by_user_id.slice(0, 8) + "…"
                                : "—"}
                            </td>
                            <td>
                              {it.created_at
                                ? new Date(it.created_at).toLocaleString()
                                : "—"}
                            </td>
                            <td>
                              {it.expires_at
                                ? new Date(it.expires_at).toLocaleString()
                                : "—"}
                            </td>
                            <td>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                }}
                              >
                                <button
                                  className="btn"
                                  onClick={async () => {
                                    try {
                                      const res = await axios.post(
                                        `/api/invites/${it.id}/resend`,
                                        {},
                                        { withCredentials: true },
                                      );
                                      const url = res.data?.accept_url;
                                      if (url) await copy(url);
                                      setNotice(
                                        url
                                          ? "New invite link generated and copied."
                                          : "Invite resent.",
                                      );
                                      await fetchInvites();
                                      await fetchActivity();
                                    } catch (e) {
                                      setError(
                                        e?.response?.data?.error ||
                                          "Failed to resend invite",
                                      );
                                    }
                                  }}
                                >
                                  Resend
                                </button>
                                <button
                                  className="btn"
                                  onClick={async () => {
                                    try {
                                      await axios.post(
                                        `/api/invites/${it.id}/revoke`,
                                        {},
                                        { withCredentials: true },
                                      );
                                      setNotice("Invite revoked.");
                                      await fetchInvites();
                                      await fetchActivity();
                                    } catch (e) {
                                      setError(
                                        e?.response?.data?.error ||
                                          "Failed to revoke invite",
                                      );
                                    }
                                  }}
                                >
                                  Revoke
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Step 2: Assignments */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card__body">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Manager assignments
              </div>
              {employees.length === 0 ? (
                <div className="edEmpty">No employees found.</div>
              ) : (
                <div className="edTableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: "30%" }}>Employee</th>
                        <th style={{ width: "30%" }}>Email</th>
                        <th style={{ width: "40%" }}>Manager</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((emp) => (
                        <tr key={emp.id}>
                          <td>{emp.name || "—"}</td>
                          <td>{emp.email || "—"}</td>
                          <td>
                            <select
                              className="input"
                              value={emp.manager_id || ""}
                              disabled={savingId === emp.id}
                              onChange={(e) =>
                                setManager(emp.id, e.target.value)
                              }
                              style={{ maxWidth: 360 }}
                            >
                              {managerOptions.map((m) => (
                                <option key={m.id || "none"} value={m.id}>
                                  {m.name}
                                </option>
                              ))}
                            </select>
                            {savingId === emp.id ? (
                              <span
                                style={{
                                  marginLeft: 10,
                                  opacity: 0.7,
                                  fontSize: 13,
                                }}
                              >
                                Saving…
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Step 3: CSV import */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card__body">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Import managers or employees via CSV
              </div>
              <div className="edReadOnly" style={{ marginBottom: 10 }}>
                CSV columns: <code>email</code> (required), <code>role</code>{" "}
                (optional; use team_manager or user),
                <code> manager_email</code> (optional; only for employees).
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  disabled={importBusy}
                />
                <button
                  className="btn primary"
                  onClick={runImport}
                  disabled={importBusy || !importFile}
                >
                  {importBusy ? "Uploading…" : "Upload CSV"}
                </button>
              </div>

              {importResult?.created?.length ? (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Created invite links
                  </div>
                  <div className="edTableWrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Invite link</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.created.map((c) => (
                          <tr key={c.invite_id}>
                            <td>{c.email}</td>
                            <td>{c.role}</td>
                            <td>
                              <a
                                href={c.accept_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Open
                              </a>
                              <button
                                className="btn"
                                style={{ marginLeft: 10 }}
                                onClick={() => copy(c.accept_url)}
                              >
                                Copy
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              {importResult?.errors?.length ? (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    Import errors
                  </div>
                  <div className="edTableWrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Line</th>
                          <th>Email</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.errors.map((e, idx) => (
                          <tr key={idx}>
                            <td>{e.line || "—"}</td>
                            <td>{e.email || "—"}</td>
                            <td>{e.error || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Step 3: Policies */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card__body">
              <div style={{ fontWeight: 700, marginBottom: 8 }}>
                Onboarding policies
              </div>
              <div className="edReadOnly" style={{ marginBottom: 10 }}>
                These policies prevent mis-invites and protect your tenant.
                Domain allowlist is optional.
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "end",
                }}
              >
                <div style={{ minWidth: 360, flex: "1 1 360px" }}>
                  <label className="label">
                    Allowed email domains (comma-separated)
                  </label>
                  <input
                    className="input"
                    value={domains}
                    onChange={(e) => setDomains(e.target.value)}
                    placeholder="example.com, subsidiary.com"
                    disabled={policiesBusy}
                  />
                </div>

                <div style={{ minWidth: 180 }}>
                  <label className="label">Invites per user per day</label>
                  <input
                    className="input"
                    type="number"
                    value={quota}
                    onChange={(e) => setQuota(e.target.value)}
                    min={1}
                    max={10000}
                    disabled={policiesBusy}
                  />
                </div>

                <div style={{ minWidth: 220 }}>
                  <label className="label">Approval required (future)</label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      height: 40,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={approvalsRequired}
                      onChange={(e) => setApprovalsRequired(e.target.checked)}
                      disabled={policiesBusy}
                    />
                    <span className="edReadOnly">
                      Store flag only (no manual workflow yet)
                    </span>
                  </div>
                </div>

                <button
                  className="btn primary"
                  onClick={savePolicies}
                  disabled={policiesBusy}
                >
                  {policiesBusy ? "Saving…" : "Save policies"}
                </button>
              </div>
            </div>
          </div>

          {/* Step 3: Activity */}
          <div className="card">
            <div className="card__body">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div style={{ fontWeight: 700 }}>Recent activity</div>
                <button
                  className="btn"
                  onClick={refreshActivity}
                  disabled={activityBusy}
                >
                  {activityBusy ? "Refreshing…" : "Refresh"}
                </button>
              </div>

              {activity.length === 0 ? (
                <div className="edEmpty" style={{ marginTop: 12 }}>
                  No activity yet.
                </div>
              ) : (
                <div className="edTableWrap" style={{ marginTop: 12 }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: "25%" }}>Time</th>
                        <th style={{ width: "25%" }}>Event</th>
                        <th style={{ width: "50%" }}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activity.map((a) => (
                        <tr key={a.id}>
                          <td>
                            {a.created_at
                              ? new Date(a.created_at).toLocaleString()
                              : "—"}
                          </td>
                          <td>{a.event_type}</td>
                          <td>
                            <code
                              style={{
                                background: "var(--card-soft)",
                                padding: 6,
                                borderRadius: 8,
                                display: "inline-block",
                              }}
                            >
                              {JSON.stringify(a.event_data || {})}
                            </code>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Appreciation review queue */}
          <div className="card" style={{ marginTop: 16 }}>
            <div className="card__body">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div style={{ fontWeight: 700 }}>Appreciation review queue</div>
                <button
                  className="btn"
                  onClick={async () => {
                    try {
                      setReviewsBusy(true);
                      await fetchReviews();
                    } finally {
                      setReviewsBusy(false);
                    }
                  }}
                  disabled={reviewsBusy}
                >
                  {reviewsBusy ? "Refreshing…" : "Refresh"}
                </button>
              </div>
              <div className="edReadOnly" style={{ marginTop: 8, marginBottom: 12 }}>
                Low-confidence transcript recognitions are queued here for manager/admin approval.
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  alignItems: "end",
                  marginBottom: 12,
                }}
              >
                <div style={{ minWidth: 160 }}>
                  <label className="label">Status</label>
                  <select
                    className="input"
                    value={reviewStatus}
                    onChange={(e) => setReviewStatus(e.target.value)}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div style={{ minWidth: 150 }}>
                  <label className="label">Mode</label>
                  <select
                    className="input"
                    value={reviewMode}
                    onChange={(e) => setReviewMode(e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="ai">AI</option>
                    <option value="rules">Rules</option>
                  </select>
                </div>
                <div style={{ minWidth: 230 }}>
                  <label className="label">Recipient</label>
                  <input
                    className="input"
                    value={reviewRecipientQuery}
                    onChange={(e) => setReviewRecipientQuery(e.target.value)}
                    placeholder="name or email"
                  />
                </div>
                <div style={{ minWidth: 170 }}>
                  <label className="label">Min confidence</label>
                  <input
                    className="input"
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={reviewMinConfidence}
                    onChange={(e) => setReviewMinConfidence(e.target.value)}
                    placeholder="0.00 to 1.00"
                  />
                </div>
                <button
                  className="btn btn--secondary"
                  type="button"
                  onClick={async () => {
                    try {
                      setReviewsBusy(true);
                      await fetchReviews();
                    } finally {
                      setReviewsBusy(false);
                    }
                  }}
                  disabled={reviewsBusy}
                >
                  Apply filters
                </button>
              </div>

              {reviews.length === 0 ? (
                <div className="edEmpty">No pending recognition reviews.</div>
              ) : (
                <div className="edTableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Created</th>
                        <th>Recipient</th>
                        <th>Sender</th>
                        <th>Confidence</th>
                        <th>Quote</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviews.map((r) => (
                        <tr key={r.id}>
                          <td>{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                          <td>{r.recipient_name || r.recipient_user_id}</td>
                          <td>{r.sender_name || r.sender_user_id || "Unknown"}</td>
                          <td>{(Number(r.confidence || 0) * 100).toFixed(1)}%</td>
                          <td style={{ maxWidth: 380 }}>{r.quote}</td>
                          <td>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button
                                className="btn btn--primary btn--sm"
                                onClick={() => decideReview(r.id, "approve")}
                              >
                                Approve
                              </button>
                              <button
                                className="btn btn--secondary btn--sm"
                                onClick={() => decideReview(r.id, "reject")}
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
}

